import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import type { DocsService } from '@affine/core/modules/doc';
import type { TagService } from '@affine/core/modules/tag';
import type { WorkbenchService } from '@affine/core/modules/workbench';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import { Text } from '@blocksuite/affine/store';

import { BRANCHES_CHANGED_EVENT, type CuriesciousActionDetail } from '../types';
import { applyBranchLink, removeBranchLink } from './branch-link-format';

function fireBranchesChanged() {
  try {
    window.dispatchEvent(new CustomEvent(BRANCHES_CHANGED_EVENT));
  } catch {
    /* SSR / detached host — no-op */
  }
}

/**
 * Workspace-tag metadata for branches. Keeps every spawned doc
 * filterable in the sidebar (search "Branch" → list every branch).
 * Idempotent: `ensureBranchTag` reuses an existing tag if found.
 */
const BRANCH_TAG_LABEL = 'Branch';
const BRANCH_TAG_COLOR = '#0F766E'; // teal — matches the source-marker accent

/**
 * Shape persisted to the branch doc's `custom:branchMeta` property.
 * This is the *single source of truth* for the branch relationship.
 * Inline body content (banner paragraph, native [[parent]] reference,
 * source-side highlight) are derived affordances — they can be edited
 * or deleted by the user without losing the relationship itself.
 */
export interface BranchMeta {
  /** Doc the branch was spawned from. */
  parentDocId: string;
  /** Block id of the source passage (may be empty for multi-block sels). */
  parentBlockId?: string;
  /** Inline range of the selection inside the source block. */
  sourceRange?: { index: number; length: number };
  /**
   * Verbatim snippet of the selection at create-time. Frozen — so
   * even if the source passage is later deleted, the branch still
   * shows what it was originally branched from.
   */
  sourcePassage: string;
  /** ISO timestamp. */
  createdAt: string;
}

export interface CreateBranchOptions {
  workspace: WorkspaceService;
  docsService: DocsService;
  tagService: TagService;
  workbench: WorkbenchService;
  editor: AffineEditorContainer | null;
  detail: CuriesciousActionDetail;
}

export interface CreateBranchResult {
  branchDocId: string;
  branchTitle: string;
  undo: () => void;
  /** Re-opens the branch beside the source (used by the toast Open button). */
  openBeside: () => void;
}

const TITLE_LIMIT = 60;

function deriveBranchTitle(passage: string): string {
  const oneLine = passage.replace(/\s+/g, ' ').trim();
  const trimmed =
    oneLine.length > TITLE_LIMIT
      ? `${oneLine.slice(0, TITLE_LIMIT).trimEnd()}\u2026`
      : oneLine;
  return `Branch: ${trimmed}`;
}

function ensureBranchTag(
  tagService: TagService
): { tag: (docId: string) => void } | null {
  try {
    const existing = tagService.tagList.tags$.value.find(
      t => t.value$.value === BRANCH_TAG_LABEL
    );
    if (existing) {
      return { tag: (id: string) => existing.tag(id) };
    }
    const created = tagService.tagList.createTag(
      BRANCH_TAG_LABEL,
      BRANCH_TAG_COLOR
    );
    return { tag: (id: string) => created.tag(id) };
  } catch (err) {
    console.warn('[create-branch] tag creation failed', err);
    return null;
  }
}

/**
 * Fork the current doc into a new linked sub-doc seeded with the
 * user's selected passage. Opens the branch beside the source so the
 * researcher can read + write at the same time.
 *
 * Storage model (three layers, one truth):
 *   1. `branchMeta` custom property on the branch doc — single source
 *      of truth; survives text edits and source-passage deletion.
 *   2. Native AffineOS doc reference from the branch back to the
 *      parent (via `docsService.addLinkedDoc`) — populates the
 *      parent's "linked references" panel for free.
 *   3. Inline marker on the source text range — teal background +
 *      `curiescious-branch://` link — the user-visible affordance.
 */
export async function createBranchFromSelection(
  opts: CreateBranchOptions
): Promise<CreateBranchResult> {
  const { workspace, docsService, tagService, workbench, editor, detail } = opts;

  if (!editor?.host?.std?.store) {
    throw new Error(
      'No active editor — open a doc before creating a branch.'
    );
  }
  const sourceDoc = editor.host.std.store;
  const sourceDocId = (sourceDoc as { id?: string }).id;
  if (!sourceDocId) {
    throw new Error('Source doc has no id — cannot branch.');
  }

  // 1. Create the branch doc.
  const branchRecord = docsService.createDoc({ primaryMode: 'page' });
  const branchDocId = branchRecord.id;

  // 2. Set the auto-title BEFORE opening so the sidebar entry already
  // reads sensibly when the workbench mounts the new pane.
  const branchTitle = deriveBranchTitle(detail.text);
  try {
    await docsService.changeDocTitle(branchDocId, branchTitle);
  } catch (err) {
    console.warn('[create-branch] failed to set branch title', err);
  }

  // 3. Persist the structured branch metadata. JSON-encoded because
  // the doc-properties API stores plain strings (mirrors how
  // sessionAttachments are persisted by centered-chat).
  const branchMeta: BranchMeta = {
    parentDocId: sourceDocId,
    parentBlockId: detail.sourceBlockId,
    sourceRange: detail.sourceRange,
    sourcePassage: detail.text,
    createdAt: new Date().toISOString(),
  };
  try {
    branchRecord.setCustomProperty('branchMeta', JSON.stringify(branchMeta));
  } catch (err) {
    console.warn('[create-branch] failed to persist branchMeta', err);
  }

  // 4. Tag the branch so it shows up in the workspace's "Branch"
  // filter and the sidebar entry sports a distinctive chip.
  const tag = ensureBranchTag(tagService);
  if (tag) {
    try {
      tag.tag(branchDocId);
    } catch (err) {
      console.warn('[create-branch] failed to apply Branch tag', err);
    }
  }

  // 5. Seed the body. AffineOS's `addLinkedDoc` appends a paragraph
  // with a native [[parent]] reference — that single call gives us a
  // clickable parent link AND populates the parent's backlinks panel.
  // Then we add the quote block + an empty paragraph by reaching
  // into the new doc's store directly. The seed runs AFTER
  // addLinkedDoc so the reference paragraph stays at the top.
  try {
    await docsService.addLinkedDoc(branchDocId, sourceDocId);
  } catch (err) {
    console.warn('[create-branch] addLinkedDoc failed', err);
  }
  await seedBranchBody(docsService, branchDocId, detail.text);
  // suppress unused-var warning while keeping workspace in the API
  // (we'll need it for the Phase 2 branches panel)
  void workspace;

  // 6. Paint the source-side marker so the user sees at a glance
  // that this passage has been branched off.
  if (detail.sourceBlockId && detail.sourceRange) {
    applyBranchLink(
      sourceDoc,
      detail.sourceBlockId,
      detail.sourceRange,
      branchDocId
    );
  }

  // 7. Open the branch beside the source so the researcher can read +
  // write in parallel.
  const openBeside = () => {
    try {
      workbench.workbench.openDoc(branchDocId, { at: 'beside' });
    } catch {
      // Workbench can't split (single-view mode) — fall back to a
      // plain open which lands in a new tab.
      try {
        workbench.workbench.openDoc(branchDocId);
      } catch {
        /* both failed — user can still open from the sidebar */
      }
    }
  };
  openBeside();
  fireBranchesChanged();

  const undo = () => {
    // Clear the source marker first so we don't leave a dangling
    // link to a doc we're about to delete.
    if (detail.sourceBlockId && detail.sourceRange) {
      try {
        removeBranchLink(
          sourceDoc,
          detail.sourceBlockId,
          detail.sourceRange
        );
      } catch (err) {
        console.warn('[create-branch] undo: source marker clear failed', err);
      }
    }
    try {
      // Delete the branch doc via the docs service (it handles the
      // collection-level removal AND meta cleanup, vs. reaching into
      // the BlockSuite collection directly which leaves meta dangling).
      (
        docsService as unknown as {
          list: { setTrash: (id: string, trash: boolean) => void };
        }
      ).list.setTrash(branchDocId, true);
    } catch (err) {
      console.warn('[create-branch] undo: branch delete failed', err);
    }
    fireBranchesChanged();
  };

  return { branchDocId, branchTitle, undo, openBeside };
}

/**
 * Add the seed content (quote block + empty paragraph) to the new
 * branch's main note. Uses `docsService.open(docId)` — the same
 * pattern AffineOS's own `addLinkedDoc` uses — which gives us the
 * loaded BlockSuite doc + a `release` handle for proper cleanup.
 *
 * The seed runs AFTER `addLinkedDoc` (which appended a `[[parent]]`
 * reference paragraph), so the final order is:
 *   [0] [[parent]] reference paragraph
 *   [1] quote block of the selected passage
 *   [2] empty paragraph for the user's cursor
 */
async function seedBranchBody(
  docsService: DocsService,
  branchDocId: string,
  passage: string
): Promise<void> {
  const opened = (
    docsService as unknown as {
      open: (id: string) => {
        doc: {
          waitForSyncReady: () => Promise<void>;
          addPriorityLoad: (n: number) => () => void;
          blockSuiteDoc: {
            getBlocksByFlavour: (
              flavour: string
            ) => Array<{ id: string; model: unknown }>;
            addBlock: (
              flavour: string,
              props: Record<string, unknown>,
              parentId?: string
            ) => string;
            transact: (fn: () => void) => void;
          };
        };
        release: () => void;
      };
    }
  ).open(branchDocId);

  const dispose = opened.doc.addPriorityLoad(10);
  try {
    await opened.doc.waitForSyncReady();
    dispose();
    const bsDoc = opened.doc.blockSuiteDoc;
    const notes = bsDoc.getBlocksByFlavour('affine:note');
    const noteId = notes[0]?.id;
    if (!noteId) {
      console.warn(
        '[create-branch] branch doc has no affine:note block — skipping seed'
      );
      return;
    }
    bsDoc.transact(() => {
      // Quote block carrying the selected passage. Type-tagged so
      // BlockSuite's native quote styling kicks in. The `text` prop
      // is a Y.Text in the schema — plain strings are accepted but
      // never render, so we wrap in `new Text(...)`.
      bsDoc.addBlock(
        'affine:paragraph',
        {
          type: 'quote',
          text: new Text(passage.replace(/\s+/g, ' ').trim()),
        },
        noteId
      );
      // Empty paragraph for the cursor.
      bsDoc.addBlock('affine:paragraph', {}, noteId);
    });
  } catch (err) {
    console.warn('[create-branch] failed to seed branch body', err);
  } finally {
    opened.release();
  }
}
